import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import { ActionSection, ModalHeader, SideCancelBtn, SubmitBtn } from "../Styles/InputStyles";

const FilterModalCommission = ({
    onApplyFilter,
    handleClose,
    currentFilter = "",
    handleClearFilters,
    staffList
}) => {
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        if (!currentFilter) return;

        if (typeof currentFilter === 'string') {
            const staff = staffList.find(s => String(s.id) === currentFilter);
            if (staff) {
                setSelectedStaff([{
                    value: String(staff.id),
                    label: staff.name
                }]);
            }
        }
        else if (currentFilter.collectedBy) {
            setSelectedStaff(currentFilter.collectedBy);
        }
    }, [currentFilter, staffList]);

    useEffect(() => {
        const resize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    const handleApply = () => {
        if (selectedStaff.length === 0) {
            toast.error("Please select at least one staff member.");
            return;
        }
        onApplyFilter({ collectedBy: selectedStaff });
    };

    const handleClearAllFilters = () => {
        setSelectedStaff([]);
        if (handleClearFilters) {
            handleClearFilters();
        }
    };

    const toggleStaff = (staff) => {
        setSelectedStaff(prev => {
            const isSelected = prev.some(s => s.value === staff.value);
            if (isSelected) {
                return prev.filter(s => s.value !== staff.value);
            } else {
                return [...prev, staff];
            }
        });
    };

    const isStaffSelected = (staffValue) => {
        return selectedStaff.some(s => s.value === staffValue);
    };

    const staffOptions = staffList.map(item => ({
        value: String(item.id),
        label: item.name
    }));

    return (
        <Container style={{ height }}>
            {/* Header */}
            <ModalHeader>
                <h1 className="text-lg font-medium text-white">Filters</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            {/* Body */}
            <Body>
                {/* LEFT – Filter Types */}
                <LeftPane>
                    <TypeItem active={true}>
                        <div className="flex justify-between items-center w-full">
                            <span>Staff</span>
                        </div>
                    </TypeItem>
                </LeftPane>

                {/* RIGHT – Staff Options */}
                <RightPane>
                    <div className="flex justify-between items-center mb-4">
                        <FilterTitle>
                            Select Staff Members
                        </FilterTitle>
                    </div>

                    {staffOptions.map(option => {
                        const checked = isStaffSelected(option.value);

                        return (
                            <OptionRow
                                key={option.value}
                                onClick={() => toggleStaff(option)}
                            >
                                <div className="flex items-center gap-3">
                                    <Checkbox checked={checked}>
                                        {checked && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </Checkbox>
                                    <span className={`leading-tight text-sm ${checked ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
                                        {option.label}
                                    </span>
                                </div>
                            </OptionRow>
                        );
                    })}
                </RightPane>
            </Body>

            <ActionSection>
                <SideCancelBtn type="button" onClick={handleClearAllFilters}>
                    Clear All
                </SideCancelBtn>
                <SubmitBtn type="submit" onClick={handleApply}>
                    Apply Filters
                </SubmitBtn>
            </ActionSection>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-primary-color px-4 py-4`;
const Body = tw.div`flex flex-1 overflow-hidden`;
const LeftPane = tw.div`w-1/3 bg-gray-50 border-r overflow-y-auto`;
const TypeItem = tw.div`px-4 py-4 cursor-pointer text-md bg-blue-50 text-primary-color font-medium border-l-4 border-primary-color`;
const RightPane = tw.div`flex-1 py-4 pl-4 pr-6 overflow-y-auto mb-16`;
const OptionRow = tw.div`flex items-center justify-between py-3 px-2 cursor-pointer rounded-md hover:bg-gray-50 transition-colors duration-150`;
const FilterTitle = tw.h3`text-md font-medium text-gray-700`;
const Checkbox = tw.div`
  w-4 h-4 border-2 rounded flex items-center justify-center
  transition-colors duration-150
  ${({ checked }) => checked
        ? 'bg-primary-color border-primary-color'
        : 'border-gray-300 bg-white hover:border-gray-400'}
`;

export default FilterModalCommission;